import { useEffect, useMemo, useState } from "react";
import { PageContent, PageHeader, PageShell } from "../../components/layout/PageShell";
import { reportSchedulesApi, reportsApi } from "../../services/apiClient";
import { COLORS, RADIUS } from "../../shared/theme";
import { reportGenerateSuccessToastMessage } from "./reportShared";

const FREQUENCIES = [
  { value: "monthly", label: "Mensuelle" },
  { value: "quarterly", label: "Trimestrielle" },
  { value: "semiannual", label: "Semestrielle" },
  { value: "annual", label: "Annuelle" },
];

function toDatetimeLocalValue(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDatetimeLocalValue(local) {
  if (!local || !local.trim()) return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

export default function PageReportsConfig({ onToast, onGenerateRaci, onOpenListPage, tenantId = null }) {
  const [reportTypes, setReportTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [savingType, setSavingType] = useState(null);
  const [scheduleEdits, setScheduleEdits] = useState({});

  const loadTypes = async () => {
    setLoading(true);
    try {
      const data = await reportsApi.list();
      setReportTypes(Array.isArray(data?.types) ? data.types : []);
    } catch (error) {
      onToast?.("error", error.message || "Impossible de charger le catalogue des rapports");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  useEffect(() => {
    const next = {};
    for (const t of reportTypes) {
      if (t.automatedSchedule) {
        const s = t.automatedSchedule;
        next[t.type] = {
          enabled: s.enabled !== false,
          frequency: s.frequency || s.defaultFrequency || "annual",
          nextLocal: toDatetimeLocalValue(s.nextRunAt),
        };
      }
    }
    setScheduleEdits(next);
  }, [reportTypes]);

  const typesForUi = useMemo(
    () =>
      reportTypes.length > 0
        ? reportTypes
        : [
            {
              type: "raci",
              label: "RACI RGPD – Rôles & responsabilités",
              category: "Gouvernance",
              periodicity: "Selon votre organisation (souvent annuel ou à tout changement majeur).",
              nomenclaturePrefix: "G1",
            },
          ],
    [reportTypes]
  );

  const typesByCategory = useMemo(() => {
    const map = new Map();
    for (const t of typesForUi) {
      const c = t.category || "Autres";
      if (!map.has(c)) map.set(c, []);
      map.get(c).push(t);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0], "fr"));
  }, [typesForUi]);

  const handleGenerateByType = async (type, meta) => {
    if (meta?.generatable === false) return;
    setGenerating(true);
    try {
      if (type === "raci" && onGenerateRaci) {
        await onGenerateRaci();
        await loadTypes();
        return;
      }
      const data = await reportsApi.generate(type);
      onToast?.("success", reportGenerateSuccessToastMessage(data));
      await loadTypes();
      onOpenListPage?.();
    } catch (error) {
      onToast?.("error", error.message || "Impossible de générer le rapport");
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveSchedule = async (type) => {
    const edit = scheduleEdits[type];
    if (!edit) return;
    setSavingType(type);
    try {
      await reportSchedulesApi.update(type, {
        enabled: edit.enabled,
        frequency: edit.frequency,
        next_run_at: fromDatetimeLocalValue(edit.nextLocal),
      });
      onToast?.("success", "Planification enregistrée");
      await loadTypes();
    } catch (error) {
      onToast?.("error", error.message || "Enregistrement impossible");
    } finally {
      setSavingType(null);
    }
  };

  const handleResetSchedule = async (type) => {
    setSavingType(type);
    try {
      await reportSchedulesApi.update(type, { reset: true });
      onToast?.("success", "Planning par défaut du catalogue rétabli");
      await loadTypes();
    } catch (error) {
      onToast?.("error", error.message || "Réinitialisation impossible");
    } finally {
      setSavingType(null);
    }
  };

  const updateEdit = (type, partial) => {
    setScheduleEdits((prev) => ({
      ...prev,
      [type]: { ...prev[type], ...partial },
    }));
  };

  return (
    <PageShell>
      <PageHeader
        title="Configuration des rapports"
        subtitle={
          <>
            Choisissez la fréquence (mensuelle à annuelle) et la date de la prochaine génération automatique. Le serveur exécute{" "}
            <code>php artisan schedule:run</code> chaque minute ; la commande <code>reports:run-due</code> lance
            les rapports dont l’échéance est atteinte. Fuseau : celui du serveur Laravel.
            {onOpenListPage ? (
              <>
                {" "}
                <button
                  type="button"
                  className="page-shell__btn page-shell__btn--ghost"
                  onClick={onOpenListPage}
                  style={{ padding: 0, border: "none", background: "none", textDecoration: "underline", fontWeight: 700 }}
                >
                  Liste des rapports générés
                </button>
              </>
            ) : null}
          </>
        }
      />

      <PageContent>
        <div style={{ maxWidth: 1120, margin: "0 auto" }}>
        <section
          style={{
            background: "#fff",
            borderRadius: 16,
            border: "1px solid #e2e8f0",
            padding: "20px 22px 22px",
            marginBottom: 24,
            boxShadow: "0 1px 3px rgba(15,23,42,0.06)",
          }}
        >
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", justifyContent: "space-between", gap: 10, marginBottom: 18 }}>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: "#0f172a" }}>Types de rapports</h2>
            <span style={{ fontSize: 12, color: "#64748b", maxWidth: 520, lineHeight: 1.45 }}>
              Planification automatique et génération manuelle. Laissez la date vide à l’enregistrement pour utiliser une première échéance calculée par le serveur.
            </span>
          </div>
          {loading ? (
            <div style={{ padding: 24, textAlign: "center", color: "#64748b" }}>Chargement du catalogue…</div>
          ) : (
            typesByCategory.map(([category, types]) => (
              <div key={category} style={{ marginBottom: 22 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 800,
                    color: "#64748b",
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    marginBottom: 12,
                    paddingBottom: 6,
                    borderBottom: "1px solid #f1f5f9",
                  }}
                >
                  {category}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 14 }}>
                  {types.map((t) => {
                    const sched = t.automatedSchedule;
                    const edit = scheduleEdits[t.type];
                    const hasSchedule = Boolean(sched);
                    const pillColor = sched?.enabled === false ? "#991b1b" : "#047857";
                    const pillBg = sched?.enabled === false ? "#fee2e2" : "#ecfdf5";
                    const pillBorder = sched?.enabled === false ? "#fecaca" : "#a7f3d0";
                    const defFreqLabel = FREQUENCIES.find((f) => f.value === sched?.defaultFrequency)?.label;
                    const genBlocked = t.generatable === false;

                    return (
                      <div
                        key={t.type}
                        style={{
                          border: "1px solid #e5e7eb",
                          borderRadius: 14,
                          padding: "14px 16px 16px",
                          background: "#fafafa",
                          display: "flex",
                          flexDirection: "column",
                          gap: 10,
                          minHeight: 0,
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontWeight: 800, fontSize: 14, color: "#0f172a", lineHeight: 1.35 }}>
                          {t.nomenclaturePrefix ? (
                            <span
                              title="Préfixe nomenclature (nom du fichier PDF)"
                              style={{
                                flexShrink: 0,
                                fontSize: 11,
                                fontWeight: 800,
                                letterSpacing: "0.05em",
                                background: "#e0f2fe",
                                border: "1px solid #7dd3fc",
                                color: "#0369a1",
                                padding: "2px 9px",
                                borderRadius: 8,
                              }}
                            >
                              {t.nomenclaturePrefix}
                            </span>
                          ) : null}
                          <span>{t.label || t.type}</span>
                        </div>
                        {t.periodicity ? (
                          <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.45 }}>{t.periodicity}</div>
                        ) : null}
                        {sched?.label ? (
                          <div
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: pillColor,
                              background: pillBg,
                              border: `1px solid ${pillBorder}`,
                              padding: "6px 10px",
                              borderRadius: 8,
                              lineHeight: 1.35,
                            }}
                          >
                            {sched.label}
                          </div>
                        ) : null}

                        {hasSchedule && edit ? (
                          <div style={{ borderTop: "1px solid #e5e7eb", paddingTop: 10, marginTop: 2 }}>
                            <div style={{ fontSize: 11, fontWeight: 800, color: "#475569", marginBottom: 8 }}>Planification automatique</div>
                            <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, fontSize: 12, cursor: "pointer" }}>
                              <input
                                type="checkbox"
                                checked={edit.enabled}
                                onChange={(e) => updateEdit(t.type, { enabled: e.target.checked })}
                              />
                              Activer la génération automatique
                            </label>
                            <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#64748b", marginBottom: 4 }}>Fréquence</label>
                            <select
                              value={edit.frequency}
                              onChange={(e) => updateEdit(t.type, { frequency: e.target.value })}
                              disabled={!edit.enabled || savingType === t.type}
                              style={{
                                width: "100%",
                                marginBottom: 10,
                                padding: "8px 10px",
                                borderRadius: 8,
                                border: "1px solid #cbd5e1",
                                fontSize: 13,
                                background: edit.enabled ? "#fff" : "#f1f5f9",
                              }}
                            >
                              {FREQUENCIES.map((f) => (
                                <option key={f.value} value={f.value}>
                                  {f.label}
                                </option>
                              ))}
                            </select>
                            <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "#64748b", marginBottom: 4 }}>Prochaine génération</label>
                            <input
                              type="datetime-local"
                              value={edit.nextLocal}
                              onChange={(e) => updateEdit(t.type, { nextLocal: e.target.value })}
                              disabled={!edit.enabled || savingType === t.type}
                              style={{
                                width: "100%",
                                fontSize: 13,
                                padding: "8px 10px",
                                borderRadius: 8,
                                border: "1px solid #cbd5e1",
                                marginBottom: 6,
                                background: edit.enabled ? "#fff" : "#f1f5f9",
                              }}
                            />
                            {defFreqLabel ? (
                              <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 8 }}>
                                Fréquence par défaut catalogue : {defFreqLabel}
                              </div>
                            ) : null}
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                              <button
                                type="button"
                                onClick={() => handleSaveSchedule(t.type)}
                                disabled={savingType === t.type || generating}
                                style={{
                                  padding: "8px 12px",
                                  borderRadius: 8,
                                  border: "none",
                                  background: savingType === t.type ? "#94a3b8" : "#0f766e",
                                  color: "#fff",
                                  fontWeight: 700,
                                  fontSize: 12,
                                  cursor: savingType === t.type ? "wait" : "pointer",
                                }}
                              >
                                {savingType === t.type ? "Enregistrement…" : "Enregistrer la planification"}
                              </button>
                              <button
                                type="button"
                                onClick={() => handleResetSchedule(t.type)}
                                disabled={savingType === t.type}
                                style={{
                                  padding: "8px 12px",
                                  borderRadius: 8,
                                  border: "1px solid #cbd5e1",
                                  background: "#fff",
                                  color: "#475569",
                                  fontWeight: 700,
                                  fontSize: 12,
                                  cursor: "pointer",
                                }}
                              >
                                Rétablir le défaut
                              </button>
                            </div>
                          </div>
                        ) : null}

                        {genBlocked && t.generatabilityReason ? (
                          <div
                            style={{
                              marginTop: "auto",
                              fontSize: 11,
                              color: "#b45309",
                              background: "#fffbeb",
                              border: "1px solid #fcd34d",
                              borderRadius: 8,
                              padding: "8px 10px",
                              lineHeight: 1.45,
                            }}
                          >
                            {t.generatabilityReason}
                          </div>
                        ) : null}

                        <button
                          type="button"
                          title={
                            genBlocked && t.generatabilityReason
                              ? t.generatabilityReason
                              : t.category
                                ? `${t.category} — ${t.label || t.type}`
                                : t.label || t.type
                          }
                          onClick={() => handleGenerateByType(t.type, t)}
                          disabled={generating || genBlocked}
                          style={{
                            marginTop: genBlocked && t.generatabilityReason ? 8 : "auto",
                            padding: "10px 14px",
                            borderRadius: RADIUS.md,
                            border: "none",
                            background: generating ? "#94a3b8" : genBlocked ? "#cbd5e1" : "#1d4ed8",
                            color: "#fff",
                            fontWeight: 800,
                            fontSize: 13,
                            cursor: generating ? "wait" : genBlocked ? "not-allowed" : "pointer",
                          }}
                        >
                          {generating ? "Génération…" : genBlocked ? "Génération indisponible" : "Générer maintenant"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </section>
        </div>
      </PageContent>
    </PageShell>
  );
}
